var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import { chain, groupBy, map } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrgType, Role } from '../../enum';
import { useAPI } from '../../state';
import { useUserSessionAPI } from '../app/selectors';
import { useAsyncDownload } from '../hooks';
var USER_API = '/api/user';
var USER_DETAIL_API = '/api/user/{id}';
var GET_COUNTRY_API = '/api/country';
var GET_INDUSTRY_API = '/api/industry';
var ORG_API = '/api/org';
var ORG_DETAIL_API = '/api/org/{id}';
var ORG_STATUS_API = '/api/org/orgStatus/{id}';
var BILLING_CONFIG_API = '/api/billingGenesys/{id}';
var RESET_PASSWORD_API = '/api/user/resetPassword/{id}';
var CHANGE_STATUS_API = '/api/user/changeStatus';
var CREATE_EXPORT_LOG_API = '/api/report/audit/export';
var CREATE_BILLING_LOG_API = '/api/report/billing/export';
var selectCompanies = function (orgs) { return orgs; };
var selectCompanyCountries = createSelector(selectCompanies, function (orgs) {
    return chain(orgs)
        .filter('country')
        .map(function (_a) {
        var id = _a.id, orgName = _a.name, country = _a.country;
        return ({ id: id, name: country === null || country === void 0 ? void 0 : country.name, orgName: orgName });
    })
        .groupBy('orgName')
        .value();
});
var selectCompaniesByName = createSelector(selectCompanies, function (orgs) { return groupBy(orgs, 'name'); });
var selectCompanyNames = createSelector(selectCompaniesByName, function (orgs) { return map(orgs, function (_a) {
    var org = _a[0];
    return org;
}); });
var selectCompanyById = createSelector(selectCompanies, selectCompanyCountries, selectCompaniesByName, function (orgs, countries, orgsByName) {
    return chain(orgs)
        .map(function (org) {
        var _a;
        return (__assign(__assign({}, org), { subscriptionStartDate: moment(org.subscriptionStartDate), subscriptionEndDate: moment(org.subscriptionEndDate), countries: (_a = countries[org.name]) !== null && _a !== void 0 ? _a : [], orgs: orgsByName[org.name] }));
    })
        .keyBy('id')
        .value();
});
export var useUsersAPI = function () {
    return useAPI(USER_API);
};
export var useUserAPI = function (id) {
    return useAPI(USER_DETAIL_API, { id: id });
};
export var useCompaniesAPI = function (orgType) {
    if (orgType === void 0) { orgType = OrgType.Cinareo; }
    var url = "".concat(ORG_API, "?orgType=").concat(orgType);
    return useAPI(url, { orgType: orgType });
};
export var useCompanyAPI = function (id) {
    return useAPI(ORG_DETAIL_API, { id: id });
};
export var useOrgStatusAPI = function (id) {
    return useAPI(ORG_STATUS_API, { id: id });
};
export var useBillingConfigAPI = function (id) {
    return useAPI(BILLING_CONFIG_API, { id: id });
};
export var useUpdateBillingConfigAPI = function (id) {
    return useAPI(BILLING_CONFIG_API, { id: id }, 'PUT');
};
export var useCountryAPI = function () {
    return useAPI(GET_COUNTRY_API);
};
export var useIndustryAPI = function () {
    return useAPI(GET_INDUSTRY_API);
};
export var useAddCompanyAPI = function () {
    return useAPI(ORG_API, {}, 'POST');
};
export var useUpdateCompanyAPI = function (id) {
    return useAPI(ORG_DETAIL_API, { id: id }, 'PUT');
};
export var useCreateUserAPI = function () {
    return useAPI(USER_API, {}, 'POST');
};
export var useUpdateUserAPI = function (id) {
    return useAPI(USER_DETAIL_API, { id: id }, 'PUT');
};
export var useResetPasswordAPI = function (id) {
    return useAPI(RESET_PASSWORD_API, { id: id }, 'PUT');
};
export var useChangeStatusAPI = function () {
    return useAPI(CHANGE_STATUS_API, {}, 'PUT');
};
export var useDeleteUserAPI = function (id) {
    return useAPI(USER_DETAIL_API, { id: id }, 'DELETE');
};
export var useCreateExportLogAPI = function () {
    return useAsyncDownload(CREATE_EXPORT_LOG_API);
};
export var useCreateBillingLogAPI = function () {
    return useAsyncDownload(CREATE_BILLING_LOG_API);
};
export var useSelectOrganizations = function (orgType) {
    var t = useTranslation('manageUser').t;
    var _a = useCompaniesAPI(orgType), _b = _a[0], data = _b.data, error = _b.error, requesting = _b.requesting, fetchCompanies = _a[1], clearCompanies = _a[2];
    var organizations = useMemo(function () { return __spreadArray([{ id: -1, name: t('billingLog.allCompanies') }], (data !== null && data !== void 0 ? data : []), true); }, [data, t]);
    return [{ data: organizations, error: error, requesting: requesting }, fetchCompanies, clearCompanies];
};
export var useCompanyNames = function (orgType) {
    var companiesData = useCompaniesAPI(orgType)[0];
    return selectCompanyNames(companiesData.data);
};
export var useDefaultOrg = function () {
    var userSession = useUserSessionAPI()[0].data;
    var isSuperAdmin = Role.Superadmin === (userSession === null || userSession === void 0 ? void 0 : userSession.versionId);
    return isSuperAdmin ? undefined : userSession === null || userSession === void 0 ? void 0 : userSession.orgs[0];
};
export var useCompanyById = function (orgType, id) {
    var companiesData = useCompaniesAPI(orgType)[0];
    var defaultOrg = useDefaultOrg();
    var orgData = defaultOrg ? [defaultOrg] : companiesData.data;
    return selectCompanyById(orgData)[id !== null && id !== void 0 ? id : 0];
};
export var useIsSuperAdmin = function () {
    var userSession = useUserSessionAPI()[0].data;
    return Role.Superadmin === (userSession === null || userSession === void 0 ? void 0 : userSession.versionId);
};
// Multi-Geo access management APIs (locationsByOrg for manage users; locations?configVersionId= for configure-orgs wizard)
var ORG_LOCATIONS_API = '/api/orgConfig/locationsByOrg';
var ORG_CLIENTS_MULTI_GEO_API = '/api/orgConfig/clientsByOrg';
var ORG_LOBS_API = '/api/orgConfig/lobs';
var USER_ACCESS_API = '/api/user/{id}/access';
export var useOrgLocationsAPI = function () { return useAPI(ORG_LOCATIONS_API); };
export var useOrgMultiGeoClientsAPI = function () { return useAPI(ORG_CLIENTS_MULTI_GEO_API); };
export var useOrgLobsAPI = function () { return useAPI(ORG_LOBS_API); };
export var useUserAccessAPI = function (id) { return useAPI(USER_ACCESS_API, { id: id }); };
export var useSaveUserAccessAPI = function () { return useAPI(USER_ACCESS_API, {}, 'PUT'); };
